/**
 * Centralized role + workflow-note definitions consumed by every
 * `PayOpsUsageInstructions` card in the ryd Pay Operations section.
 *
 * Keeping this in one place means a role label change ripples to every page
 * without per-page edits.
 */

import { Roles } from '@/constants/roles';

export interface UsageInstructionRole {
  id: string;
  label?: string;
  purpose: string;
}

export interface UsageInstructionNote {
  title?: string;
  body: string;
  icon?: string;
  badgeClass?: string;
}

const VIEWER_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_viewer,
    purpose: 'read the queue, open case detail, view audit',
  },
];

const AGENT_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_agent,
    purpose: 'execute Complete / Cancel, run bulk jobs, manage incidents',
  },
];

const ADMIN_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_admin,
    purpose: 'all agent permissions plus Force Complete, Refund & Cancel, global audit',
  },
];

const PAY_OPS_REFUND_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_agent,
    purpose: 'update PSP refund status and add comments',
  },
  { id: Roles.pay_operations_admin, purpose: 'all agent permissions' },
];

export const PAY_OPS_QUEUE_ROLES: UsageInstructionRole[] = [
  ...VIEWER_ROLES,
  ...AGENT_ROLES,
  ...ADMIN_ROLES,
];

export const PAY_OPS_DETAIL_ROLES: UsageInstructionRole[] = [
  ...VIEWER_ROLES,
  ...AGENT_ROLES,
  ...ADMIN_ROLES,
];

export const PAY_OPS_AUDIT_ROLES: UsageInstructionRole[] = [...ADMIN_ROLES];

export const PAY_OPS_DRIVE_AWAY_ROLES: UsageInstructionRole[] = [...AGENT_ROLES, ...ADMIN_ROLES];

export const PAY_OPS_INCIDENT_ROLES: UsageInstructionRole[] = [
  ...VIEWER_ROLES,
  ...AGENT_ROLES,
  ...ADMIN_ROLES,
];

export const PAY_OPS_BULK_JOB_ROLES: UsageInstructionRole[] = [
  ...VIEWER_ROLES,
  ...AGENT_ROLES,
  ...ADMIN_ROLES,
];

/** Manual Assignment is gated to pay_operations_agent + pay_operations_admin only. */
export const PAY_OPS_MANUAL_ASSIGNMENT_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_agent,
    purpose: 'submit UUIDs to an incident or override a classification',
  },
  {
    id: Roles.pay_operations_admin,
    purpose: 'all agent permissions, including bulk paste of large UUID lists',
  },
];

/** Refund tracker updates are narrowed to pay_operations_* roles only (FQ-7). */
export const PAY_OPS_REFUND_TRACKER_ROLES: UsageInstructionRole[] = PAY_OPS_REFUND_ROLES;

export const PAY_OPS_ODOO_PAYMENT_ROLES: UsageInstructionRole[] = [
  {
    id: Roles.pay_operations_agent,
    purpose: 'attach a payment object to an Odoo queue.message and trigger invoicing',
  },
  {
    id: Roles.pay_operations_admin,
    purpose: 'all agent permissions',
  },
];

// ----------------------------------------------------------------------------
// Workflow note builders
// ----------------------------------------------------------------------------

export function buildQueueWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Standard',
      body: 'general stuck orders (PROBLEM / FAILED / REJECTED). Operator decides cancel vs complete.',
      icon: 'fa-folder-open-o',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Overshoot',
      body: 'pre-auth vs captured amount mismatch — usually <em>complete manually</em>.',
      icon: 'fa-balance-scale',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'EV Reconcile',
      body: 'Plugsurfing EV charging orders — usually <em>cancel</em> (bulk-first).',
      icon: 'fa-bolt',
      badgeClass: 'bg-info text-dark',
    },
    {
      title: 'Incidents',
      body: 'orders tagged with a known incident grouping. One order can belong to multiple incidents.',
      icon: 'fa-flag',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Resolved',
      body: 'closed cases — auto-healed by refresh or amended successfully.',
      icon: 'fa-check-circle',
      badgeClass: 'bg-success',
    },
    {
      title: 'Refresh',
      body: 'click <strong>Refresh from Order Service</strong> to pull new cases now. Google Cloud Scheduler also refreshes automatically.',
      icon: 'fa-refresh',
      badgeClass: 'bg-secondary',
    },
  ];
}

export function buildDetailWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Preview-then-execute',
      body: 'every mutation generates a server-side preview; the execute step uses the stored payload — you can never substitute a different draft between preview and execute.',
      icon: 'fa-eye',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Stale preview',
      body: 'if the order changes between preview and execute, the server rejects with 409 and you re-generate the preview.',
      icon: 'fa-clock-o',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Force Complete',
      body: 'bypasses normal state guards. Requires explicit acknowledgement and is recorded in the audit log.',
      icon: 'fa-shield',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Refund & Cancel',
      body: 'flags the order refund-eligible and auto-creates a pending refund-tracking record. The actual PSP refund is performed manually on the provider platform.',
      icon: 'fa-undo',
      badgeClass: 'bg-dark',
    },
    {
      title: 'Idempotency',
      body: 'each execute carries an <code>x-idempotency-key</code> derived from the preview token; a retry of the same preview does not double-charge.',
      icon: 'fa-key',
      badgeClass: 'bg-secondary',
    },
  ];
}

export function buildAuditWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Scope',
      body: 'every mutation (success and failure) writes an immutable attempt record. The same event is also forwarded to the BigQuery audit pipeline.',
      icon: 'fa-archive',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Filters',
      body: 'filter by order UUID, actor email, or action; combine with date filters for incident retrospectives.',
      icon: 'fa-filter',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Retention',
      body: 'kept indefinitely — there is no purge job in v1.',
      icon: 'fa-database',
      badgeClass: 'bg-info text-dark',
    },
  ];
}

export function buildDriveAwayWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Step 1 — receipt',
      body: 'enter the reason (min 10 chars). The reason is forwarded to the audit log and the preview.',
      icon: 'fa-pencil',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Step 2 — complete',
      body: 'executes <code>completeManually</code> against the order. Preview-then-execute is server-enforced.',
      icon: 'fa-check',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Step 3 — payment object',
      body: 'pick a default (SEPA / PayPal / Card) and fill in operator-provided bank details. The payment object is persisted to <code>driveaway_invoice_payments</code> for auditing.',
      icon: 'fa-credit-card',
      badgeClass: 'bg-info text-dark',
    },
    {
      title: 'Partial success',
      body: 'a completed order with a failed invoice trigger is surfaced loudly — never silent.',
      icon: 'fa-exclamation-triangle',
      badgeClass: 'bg-warning text-dark',
    },
  ];
}

export function buildIncidentsWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Agent-managed',
      body: 'viewers can read incident groupings. Creating, renaming, closing, or changing incident membership requires an agent or admin role.',
      icon: 'fa-flag-o',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Multi-membership',
      body: 'one order can belong to multiple incidents. The detail page shows a warning when an order is in more than one.',
      icon: 'fa-link',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Bulk-scoped',
      body: 'bulk actions can be scoped to an incident; the bulk job snapshot records membership at execution time.',
      icon: 'fa-tasks',
      badgeClass: 'bg-secondary',
    },
  ];
}

export function buildBulkJobWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Per-item preview',
      body: '3 parallel calls per batch with a 100 ms pause between batches. Each item generates its own server-side preview, so success status is the actual outcome — not just "the request was sent".',
      icon: 'fa-tasks',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Snapshot',
      body: 'partner, classification, order state, amount and currency are captured per item at job-creation time so the detail page stays meaningful after the underlying order changes.',
      icon: 'fa-camera',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Retry failures',
      body: 'admins can re-run only the failed items. Successful items are skipped on retry.',
      icon: 'fa-refresh',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Notification',
      body: 'one summary notification per bulk operation is written to the job record on completion.',
      icon: 'fa-bell',
      badgeClass: 'bg-info text-dark',
    },
  ];
}

export function buildOdooPaymentWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Find the order',
      body: 'paste the order ID. We look it up in Odoo and show you what we find.',
      icon: 'fa-search',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Prepare the invoice',
      body: "if Odoo isn't ready yet, click <strong>Ask Odoo to prepare the invoice</strong> and wait. Odoo usually responds within 30 seconds. If it takes longer than 90 seconds, click <strong>Keep waiting</strong> or come back later.",
      icon: 'fa-clock-o',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Enter the payment',
      body: 'pick a method (SEPA bank transfer, PayPal, Credit Card) or fill it in yourself. Set the type, status, amount, IBAN, reference and an optional note — these are the things Odoo needs to issue the invoice.',
      icon: 'fa-credit-card',
      badgeClass: 'bg-info text-dark',
    },
    {
      title: 'Send to Odoo',
      body: "the payment is sent to Odoo and the invoice is finished on Odoo's side. The order's other details (customer, vehicle, line items) stay as Odoo already has them.",
      icon: 'fa-paper-plane',
      badgeClass: 'bg-success',
    },
    {
      title: 'How it differs from Invoice Modification',
      body: "use this page to <strong>record or change a payment</strong>. Use <a href=\"/accounting/invoicing/invoice-modification\" target=\"_blank\" rel=\"noopener\" class=\"text-decoration-underline text-reset\"><strong>Invoice Modification</strong></a> if you need to fix the customer's name, address, or vehicle on an invoice that has already gone out.",
      icon: 'fa-exchange',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Replacing an existing payment',
      body: "if the order already has a payment on file, sending will replace it. You'll be asked to tick a box to confirm before the Send button unlocks.",
      icon: 'fa-exclamation-triangle',
      badgeClass: 'bg-warning text-dark',
    },
  ];
}

export function buildManualAssignmentWorkflowNotes(): UsageInstructionNote[] {
  return [
    {
      title: 'Step 1 — choose target',
      body: 'either an incident (groups related orders) or a classification override (forces an order into a specific case type). A reason of at least 10 characters is required and is written to the audit log.',
      icon: 'fa-bullseye',
      badgeClass: 'bg-primary',
    },
    {
      title: 'Step 2 — provide UUIDs',
      body: 'paste a single UUID or a bulk list (one per line). The list is deduplicated and validated client-side before submission.',
      icon: 'fa-pencil',
      badgeClass: 'bg-secondary',
    },
    {
      title: 'Step 3 — review and submit',
      body: 'preview the parsed UUIDs and the chosen target before confirming. Submission opens a session you can share via the result-page link.',
      icon: 'fa-check',
      badgeClass: 'bg-info text-dark',
    },
    {
      title: 'Conflicts',
      body: 'an order already assigned to a different incident — or already classified differently — is reported on the result page and skipped, not silently overwritten.',
      icon: 'fa-exclamation-triangle',
      badgeClass: 'bg-warning text-dark',
    },
    {
      title: 'Shareable session',
      body: 'each run is persisted with a session ID so progress, conflicts, and processed orders can be revisited or shared with teammates.',
      icon: 'fa-link',
      badgeClass: 'bg-secondary',
    },
  ];
}
